/**
 * Cross-tab coordination service for authentication.
 * Coordinates token clearing and refresh attempts across browser tabs.
 */

import { STORAGE_KEYS } from '../lib/storage'
import { getBrowserAdapter } from '../lib/browserAdapter'
import { systemClock, type Clock } from '../lib/clock'
import { randomIdGenerator, type IdGenerator } from '../lib/id'
import { browserScheduler, type Scheduler } from '../lib/scheduler'

/**
 * Reasons for token clearing.
 */
export type TokenClearReason = 'logout' | 'auth_error' | 'session_expired' | 'unknown'

/**
 * Token clearing event data.
 */
export interface TokenClearEvent {
    reason: TokenClearReason
    timestamp: number
    tabId: string
    errorDetails?: {
        errorType?: string
        status?: number
        message?: string
    }
}

/**
 * BroadcastChannel message types.
 */
type BroadcastMessage =
    | { type: 'TOKEN_CLEARED'; data: TokenClearEvent }
    | { type: 'REFRESH_ATTEMPT'; data: { tabId: string; timestamp: number } }
    | { type: 'REFRESH_SUCCESS'; data: { tabId: string; timestamp: number } }

/**
 * Generate a unique tab ID for this session.
 */
function generateTabId(clock: Clock, idGenerator: IdGenerator): string {
    return `tab-${clock.now()}-${idGenerator.generate()}`
}

/**
 * Get or create a tab ID stored in sessionStorage.
 */
function getTabId(
    adapter: ReturnType<typeof getBrowserAdapter>,
    clock: Clock,
    idGenerator: IdGenerator
): string {
    const key = 'auth_tab_id'
    let tabId = adapter.sessionStorage.getItem(key)
    if (!tabId) {
        tabId = generateTabId(clock, idGenerator)
        try {
            adapter.sessionStorage.setItem(key, tabId)
        } catch {
            // Ignore storage errors
        }
    }
    return tabId
}

/**
 * Cross-tab coordinator service.
 */
class CrossTabCoordinator {
    private tabId: string
    private broadcastChannel: BroadcastChannel | null = null
    private storageListener: ((e: StorageEvent) => void) | null = null
    private isInitialized = false
    private adapter: ReturnType<typeof getBrowserAdapter>
    private clock: Clock
    private scheduler: Scheduler

    constructor(
        adapter: ReturnType<typeof getBrowserAdapter> = getBrowserAdapter(),
        clock: Clock = systemClock,
        idGenerator: IdGenerator = randomIdGenerator,
        scheduler: Scheduler = browserScheduler
    ) {
        this.adapter = adapter
        this.clock = clock
        this.scheduler = scheduler
        this.tabId = getTabId(adapter, clock, idGenerator)
    }

    /**
     * Initialize the coordinator.
     * Sets up storage event listener and BroadcastChannel.
     */
    initialize(): void {
        if (this.isInitialized) return

        // Set up BroadcastChannel for cross-tab communication
        this.broadcastChannel = this.adapter.createBroadcastChannel('auth-coordination')
        if (this.broadcastChannel) {
            // Listen for messages from other tabs
            this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
                this.handleBroadcastMessage(event.data)
            }
        }

        // Set up storage event listener (fires when localStorage changes in other tabs)
        this.storageListener = (e: StorageEvent) => {
            this.handleStorageEvent(e)
        }
        this.adapter.addEventListener('storage', this.storageListener as EventListener)

        this.isInitialized = true
    }

    /**
     * Destroy the coordinator.
     * Removes event listeners.
     */
    destroy(): void {
        if (this.broadcastChannel) {
            this.broadcastChannel.close()
            this.broadcastChannel = null
        }

        if (this.storageListener) {
            this.adapter.removeEventListener('storage', this.storageListener as EventListener)
            this.storageListener = null
        }

        this.isInitialized = false
    }

    /**
     * Broadcast a token clearing event to other tabs.
     */
    broadcastTokenCleared(
        reason: TokenClearReason,
        errorDetails?: TokenClearEvent['errorDetails']
    ): void {
        const event: TokenClearEvent = {
            reason,
            timestamp: this.clock.now(),
            tabId: this.tabId,
            errorDetails,
        }

        // Broadcast via BroadcastChannel
        if (this.broadcastChannel) {
            try {
                this.broadcastChannel.postMessage({
                    type: 'TOKEN_CLEARED',
                    data: event,
                })
            } catch {
                // Ignore broadcast errors
            }
        }

        // Also store in localStorage as a marker (storage event will fire in other tabs)
        // Use a special key that we can detect
        try {
            this.adapter.localStorage.setItem(
                'auth_token_clear_event',
                JSON.stringify({ ...event, source: 'broadcast' })
            )
            // Remove immediately - the storage event already fired
            // Use scheduler for setTimeout
            this.scheduler.setTimeout(() => {
                this.adapter.localStorage.removeItem('auth_token_clear_event')
            }, 0)
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Get the current tab ID.
     */
    getTabId(): string {
        return this.tabId
    }

    /**
     * Handle storage events from other tabs.
     */
    private handleStorageEvent(e: StorageEvent): void {
        // Only handle events for auth-related keys
        if (
            e.key === STORAGE_KEYS.ACCESS_TOKEN ||
            e.key === STORAGE_KEYS.REFRESH_TOKEN ||
            e.key === STORAGE_KEYS.USER ||
            e.key === 'auth_token_clear_event'
        ) {
            // If tokens were removed, check if it was intentional
            if (
                e.key === STORAGE_KEYS.ACCESS_TOKEN ||
                e.key === STORAGE_KEYS.REFRESH_TOKEN ||
                e.key === STORAGE_KEYS.USER
            ) {
                if (e.newValue === null && e.oldValue !== null) {
                    // Token was removed - check if we have a clear event marker
                    const clearEventJson =
                        this.adapter.localStorage.getItem('auth_token_clear_event')
                    if (clearEventJson) {
                        try {
                            const clearEvent = JSON.parse(clearEventJson) as TokenClearEvent
                            // Dispatch custom event so AuthContext can handle it
                            this.adapter.window.dispatchEvent(
                                new CustomEvent('auth-tokens-cleared', {
                                    detail: clearEvent,
                                })
                            )
                        } catch {
                            // Ignore parse errors
                        }
                    }
                }
            }
        }
    }

    /**
     * Handle broadcast messages from other tabs.
     */
    private handleBroadcastMessage(message: BroadcastMessage): void {
        if (message.type === 'TOKEN_CLEARED') {
            // Dispatch custom event so AuthContext can handle it
            this.adapter.window.dispatchEvent(
                new CustomEvent('auth-tokens-cleared', {
                    detail: message.data,
                })
            )
        }
    }
}

/**
 * Singleton instance for production use.
 */
export const crossTabCoordinator = new CrossTabCoordinator()
